import React, { useState, useRef } from 'react';
import { FileText, Upload, X, Eye, Trash2, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import DocumentViewer from './DocumentViewer';
import { uploadFile, FileInfo, validateFile, compressFile } from '../../services/fileUploadApi';
import { API_URL } from '../../utils/ConfigUtils';

// Define SOWDocument interface locally
interface SOWDocument {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  created_at: string;
  stored_name?: string; // For server-stored files
}

// Helper function to correct old URL formats - SIMPLIFIED
const correctDocumentUrl = (url: string): string => {
  // If it's already a full URL, return as is
  if (url.startsWith('http')) {
    return url;
  }
  
  // For relative paths, just return as is - let DocumentViewer handle URL construction
  return url;
};

// Simple ID generator
const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
};

interface MultipleDocumentUploadProps {
  documents: SOWDocument[];
  onDocumentsChange: (documents: SOWDocument[]) => void;
  maxFileSize?: number; // in MB
  maxFiles?: number;
}

const MultipleDocumentUpload: React.FC<MultipleDocumentUploadProps> = ({
  documents,
  onDocumentsChange,
  maxFileSize = 25, // Default 25MB
  maxFiles = 5, // Default 5 files
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedDocumentForView, setSelectedDocumentForView] = useState<SOWDocument | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper function to construct backend URL dynamically - SIMPLIFIED
  const getBackendUrl = (path: string) => {
    // If it's already a full URL, return as is
    if (path.startsWith('http')) {
      return path;
    }
    
    // For relative paths, just return the path - let DocumentViewer handle URL construction
    return path;
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Check if we're exceeding the maximum number of files
    if (documents.length + files.length > maxFiles) {
      setUploadError(`You can only upload a maximum of ${maxFiles} files.`);
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    const newDocuments: SOWDocument[] = [...documents];
    const filesArray = Array.from(files);
    
    for (const file of filesArray) {
      // Validate file using the utility function
      const validation = validateFile(file, maxFileSize);
      if (!validation.isValid) {
        setUploadError(validation.error || 'Invalid file');
        setIsUploading(false);
        return;
      }

      try {
        // Processing file
        
        // Compress file if possible to reduce upload size
        const compressedFile = await compressFile(file, 10); // Compress to max 10MB
                  // File compressed
        
        // Upload file to server with progress tracking
        const response = await uploadFile(compressedFile, (progress) => {
          // Upload progress tracked
          setUploadProgress(prev => ({ ...prev, [file.name]: progress }));
        });
        
                  // Upload completed
        
        // Create new document object with server URL
        const newDocument: SOWDocument = {
          id: generateId(),
          name: file.name,
          url: getBackendUrl(response.file.url), // Full backend URL
          type: file.type,
          size: file.size,
          created_at: new Date().toISOString(),
          stored_name: response.file.stored_name // Store the server filename
        };
        
        newDocuments.push(newDocument);
        // Document added to list
      } catch (error) {
        console.error('Error uploading file:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to upload file';
        setUploadError(`Failed to upload ${file.name}: ${errorMessage}`);
        
        // Remove this file from progress tracking
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[file.name];
          return newProgress;
        });
      }
    }

    onDocumentsChange(newDocuments);
    setIsUploading(false);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };



  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    
    // Create a new file input event to reuse the same logic
    const dataTransfer = new DataTransfer();
    Array.from(files).forEach(file => dataTransfer.items.add(file));
    
    if (fileInputRef.current) {
      fileInputRef.current.files = dataTransfer.files;
      const event = new Event('change', { bubbles: true });
      fileInputRef.current.dispatchEvent(event);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleRemoveDocument = (id: string) => {
    const updatedDocuments = documents.filter(doc => doc.id !== id);
    onDocumentsChange(updatedDocuments);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-4">
      {/* Document list */}
      {documents.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-gray-300">Uploaded Documents ({documents.length}/{maxFiles})</h3>
          <div className="space-y-2">
            {documents.map(document => (
              <div 
                key={document.id} 
                className="flex items-center justify-between p-2 bg-background-darker rounded-md border border-border-dark"
              >
                <div className="flex items-center space-x-2">
                  <FileText size={16} className="text-primary" />
                  <span className="text-sm truncate max-w-xs">{document.name}</span>
                  <span className="text-xs text-gray-400">({formatFileSize(document.size)})</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="p-1 h-7 w-7"
                    onClick={() => setSelectedDocumentForView(document)}
                  >
                    <Eye size={14} />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="p-1 h-7 w-7"
                    onClick={() => handleRemoveDocument(document.id)}
                  >
                    <Trash2 size={14} className="text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload area */}
      {documents.length < maxFiles && (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isUploading 
              ? 'border-primary bg-primary/10' 
              : 'border-gray-600 hover:border-primary hover:bg-primary/5'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {isUploading ? (
            <div className="space-y-3">
              <Loader2 className="animate-spin mx-auto h-8 w-8 text-primary" />
              <div>
                <p className="text-primary font-medium">Uploading...</p>
                <p className="text-sm text-gray-400">Please wait while we process your files</p>
              </div>
              {/* Progress bars for multiple files */}
              {Object.entries(uploadProgress).map(([fileName, progress]) => (
                <div key={fileName} className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${progress}%` }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <>
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-300 mb-2">Drag and drop files here, or click to browse</p>
              <p className="text-sm text-gray-500 mb-4">
                Supported formats: PDF, Excel, CSV, Word, Images (max {maxFileSize}MB each)
              </p>
              <p className="text-xs text-yellow-400 mb-2">
                ⚠️ Large files (&gt;10MB) may take longer to upload. Images are automatically compressed.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                Choose Files
              </Button>
            </>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.webp"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      )}

      {/* Error message */}
      {uploadError && (
        <div className="text-sm text-red-500 mt-2">
          {uploadError}
        </div>
      )}

      {/* Document viewer modal */}
      {selectedDocumentForView && (
        <>
                  {/* Document viewer debug info */}
          <DocumentViewer
            isOpen={!!selectedDocumentForView}
            onClose={() => setSelectedDocumentForView(null)}
            documentUrl={correctDocumentUrl(selectedDocumentForView.url)}
            documentName={selectedDocumentForView.name}
          />
        </>
      )}
    </div>
  );
};

export default MultipleDocumentUpload;